import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht } from '@symphenia/shared-types'
import { fmtDateLong } from '@/lib/format-date'
import { useHoursFormat, formatHours, formatHoursWithUnit } from '@/lib/hours-format'
import type { TFunction } from 'i18next'
import { Settings, Check, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { ExportButton } from '@/components/ui/ExportButton'
import { useAuthStore } from '@/lib/auth.store'
import { betreffMitZeitraum } from '@/lib/mail/betreff'
import { timeEntriesApi, timeConfigApi } from '../api/time-tracking.api'
import { usersApi } from '@/lib/users/users.api'
import type { User } from '@/lib/users/user.types'
import { FilterBar } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { HILFE_VERWEISE } from '@/lib/hilfe'
import { loadUiSetting, markUiSettingApplied, saveUiSetting, saveUiSettingIfChanged } from '@/lib/ui-settings'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import type {
  DailyWorkTimeRow,
  TenantTimeConfig,
} from '../types/time-tracking.types';
import type { FilterFieldDef, ActiveFilter, ColumnFieldDef, ColumnLayout, ColumnSortConfig } from '@/components/list/list.types';
import { pageWide } from '@/lib/form-styles'

// ─── Column layout persistence ─────────────────────────────────────────────────

// 'date' is excluded here – it is always rendered as the fixed first column
// AP-19: Fehlzeiten-Spalten sind standardmäßig sichtbar (geleistete Stunden | Fehlzeiten | Beschreibung | Gesamt)
const COLUMN_LAYOUT_KEY = 'columnbar_layout_payroll'
const PAGE_SIZE_KEY = 'pagesize_payroll'

const DEFAULT_COLUMN_KEYS = ['employee', 'netMinutes', 'absenceMinutes', 'absenceDescription', 'totalWithAbsenceMinutes']

/** Vorbelegung der FilterBar: Tagesauswertung ohne Zeitraum ist sinnlos, dazu der Mitarbeiter. */
const DEFAULT_FILTER_FIELD_KEYS = ['dateFrom', 'dateTo', 'userId']

// Spalten, die als Minuten (h/min) dargestellt und in der Summenzeile aufsummiert werden
const MINUTE_COLUMN_KEYS = new Set(['totalMinutes', 'breakMinutes', 'netMinutes', 'absenceMinutes', 'totalWithAbsenceMinutes'])

const SERVER_SORTABLE_KEYS = new Set(['date', 'employee'])
// Sorted client-side (within the loaded page) – no API round-trip needed
// lv_* fields are also client-sortable but detected dynamically by prefix
const CLIENT_SORTABLE_KEYS = new Set(['vorname', 'nachname', 'entryCount', 'totalMinutes', 'breakMinutes', 'netMinutes', 'absenceMinutes', 'absenceDescription', 'totalWithAbsenceMinutes'])
const isClientSortable = (key: string) => CLIENT_SORTABLE_KEYS.has(key) || key.startsWith('lv_')
const isServerSortable = (key: string) => SERVER_SORTABLE_KEYS.has(key)
const isSortableKey    = (key: string) => isServerSortable(key) || isClientSortable(key)

// Die Einstellungs-Schlüssel heißen weiterhin `…_payroll` bzw. layoutKey="payroll":
// sie sind reine Speicheradressen. Ein Umbenennen würde die gespeicherten
// Spalten-/Filter-Layouts aller Nutzer stillschweigend verwerfen — jetzt auch
// serverseitig, weil AP-23 denselben Schlüsselraum benutzt.
function loadColumnLayout(_available: ColumnFieldDef[]): ColumnLayout {
  // AP-23: synchron aus dem Cache lesen, Cache + API (debounced) schreiben.
  // Strip only 'date' – it is always the fixed first column.
  // We intentionally do NOT filter by validKeys here so that LV-columns
  // (which need timeConfig to be loaded first) are preserved across reloads.
  // shownColumns already handles unknown keys gracefully via find+filter(Boolean).
  const parsed = loadUiSetting<ColumnLayout | null>(COLUMN_LAYOUT_KEY, null)
  const columnKeys = (parsed?.columnKeys ?? []).filter((k) => k !== 'date')
  if (columnKeys.length > 0) return {
    columnKeys,
    columnWidths: parsed?.columnWidths ?? {},
    sortConfig: parsed?.sortConfig ?? null,
  }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null }
}

function saveColumnLayout(layout: ColumnLayout) {
  saveUiSettingIfChanged(COLUMN_LAYOUT_KEY, layout)
}

// ─── Column definitions builder ────────────────────────────────────────────────

function buildAvailableColumns(
  t: TFunction,
  timeConfig: TenantTimeConfig | null,
): ColumnFieldDef[] {
  const cols: ColumnFieldDef[] = []

  // Mitarbeiter – 'date' wird als feste erste Spalte im Header gerendert und ist hier bewusst ausgelassen
  cols.push({ key: 'employee',    label: t('timeTracking.employee',  { defaultValue: 'Mitarbeiter' }), category: 'core' })
  cols.push({ key: 'vorname',     label: t('timeTracking.vorname',   { defaultValue: 'Vorname' }),     category: 'core' })
  cols.push({ key: 'nachname',    label: t('timeTracking.nachname',  { defaultValue: 'Nachname' }),    category: 'core' })

  // Zeitdaten
  cols.push({ key: 'entryCount',   label: t('timeTracking.entryCount',   { defaultValue: 'Stempelungen' }), category: 'time', align: 'right' })
  cols.push({ key: 'totalMinutes', label: t('timeTracking.totalMinutes', { defaultValue: 'Bruttozeit' }),   category: 'time', summable: true, align: 'right' })
  cols.push({ key: 'breakMinutes', label: t('timeTracking.breakMinutes', { defaultValue: 'Pause' }),        category: 'time', summable: true, align: 'right' })
  cols.push({ key: 'netMinutes',   label: t('timeTracking.netMinutes',   { defaultValue: 'Nettozeit' }),    category: 'time', summable: true, align: 'right' })

  // Fehlzeiten (AP-19)
  cols.push({ key: 'absenceMinutes',          label: t('timeTracking.absenceMinutes',   { defaultValue: 'Fehlzeiten' }),            category: 'absence', summable: true, align: 'right' })
  cols.push({ key: 'absenceDescription',      label: t('timeTracking.absenceDescription', { defaultValue: 'Fehlzeit-Beschreibung' }), category: 'absence' })
  cols.push({ key: 'totalWithAbsenceMinutes', label: t('timeTracking.totalWithAbsence', { defaultValue: 'Gesamtstunden' }),         category: 'absence', summable: true, align: 'right' })

  // LV-Felder aus DailyRecord (Typ B) – Nummernfelder bekommen summable: true
  if (timeConfig?.customFieldsSchema) {
    for (const field of timeConfig.customFieldsSchema) {
      cols.push({
        key:       `lv_${field.name}`,
        label:     field.label,
        category:  'lohnverrechnung',
        align:     field.type === 'number' ? 'right' as const : undefined,
        summable:  field.type === 'number',
        fieldType: field.type,
      })
    }
  }

  return cols
}

// ─── Filter helpers ─────────────────────────────────────────────────────────────

function buildAvailableFilterFields(
  t: TFunction,
  users: User[],
  timeConfig: TenantTimeConfig | null,
): FilterFieldDef[] {
  const fields: FilterFieldDef[] = [
    // KA-18: reines Datum — diese Liste fasst je Tag zusammen, eine Uhrzeit
    // hätte nichts zu filtern und wurde vorher stillschweigend verworfen.
    { key: 'dateFrom', label: t('timeTracking.from', { defaultValue: 'Von' }), type: 'date', category: 'core', operators: ['is'] },
    { key: 'dateTo',   label: t('timeTracking.to',   { defaultValue: 'Bis' }), type: 'date', category: 'core', operators: ['is'] },
    {
      key: 'userId',
      label: t('timeTracking.employee', { defaultValue: 'Mitarbeiter' }),
      type: 'select-multi',
      category: 'core',
      options: users.map((u) => ({
        value: u.id,
        label: u.profile?.firstName
          ? `${u.profile.firstName}${u.profile.lastName ? ' ' + u.profile.lastName : ''}`
          : u.email ?? '—',
      })),
      operators: ['is', 'is_not'],
    },
  ]

  // LV-Felder aus DailyRecord (Typ B) – client-seitig gefiltert
  if (timeConfig?.customFieldsSchema) {
    for (const field of timeConfig.customFieldsSchema) {
      if (field.type === 'checkbox') {
        fields.push({
          key: `lv_${field.name}`,
          label: field.label,
          type: 'boolean',
          category: 'lohnverrechnung',
          operators: ['is', 'is_not'],
        })
      } else if (field.type === 'number') {
        fields.push({
          key: `lv_${field.name}`,
          label: field.label,
          type: 'number',
          category: 'lohnverrechnung',
          operators: ['is', 'is_not', 'contains', 'not_contains', 'gt', 'lt'],
        })
      } else if (field.type === 'select' && field.options && field.options.length > 0) {
        fields.push({
          key: `lv_${field.name}`,
          label: field.label,
          type: 'select',
          category: 'lohnverrechnung',
          options: field.options.map((o) => ({ value: o, label: o })),
          operators: ['is', 'is_not', 'contains', 'not_contains'],
        })
      } else {
        // text, time → Texteingabe
        fields.push({
          key: `lv_${field.name}`,
          label: field.label,
          type: 'text',
          category: 'lohnverrechnung',
          operators: ['is', 'is_not', 'contains', 'not_contains'],
        })
      }
    }
  }

  return fields
}

function filtersToApiParams(filterValues: Record<string, ActiveFilter>) {
  const params: Record<string, unknown> = {}
  for (const [key, active] of Object.entries(filterValues)) {
    if (!active.value) continue
    if (key === 'dateFrom') { params['dateFrom'] = active.value.slice(0, 10); continue }
    if (key === 'dateTo')   { params['dateTo']   = active.value.slice(0, 10); continue }
    if (key === 'userId') {
      const ids = active.value.split(',').filter(Boolean)
      if (ids.length === 0) continue
      if (active.operator === 'is_not') {
        params['userIdNot'] = ids[0]
      } else {
        if (ids.length === 1) params['userId'] = ids[0]
        else params['userIds'] = ids.join(',')
      }
      continue
    }
  }
  return params
}

// ─── Category labels ────────────────────────────────────────────────────────────

const COLUMN_CATEGORY_LABELS: Record<string, string> = {
  core:             'Kerndaten',
  time:             'Zeitdaten',
  absence:          'Fehlzeiten',
  lohnverrechnung:  'Lohnverrechnungsfelder',
}

// ─── Main component ─────────────────────────────────────────────────────────────

export default function DailyWorkTimesPage() {
  const { t } = useTranslation()
  const hoursFormat = useHoursFormat()
  // KA-34: Der Export hängt am Recht, das der Server verlangt — die Seite selbst
  // öffnet schon das Leserecht, und wer nur lesen darf, bekam beim Klick 403.
  const { user } = useAuthStore()
  const canExport = erfuelltRecht('time.daily_work_time.export', user?.permissions)

  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})

  // Pagination
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(() => {
    const v = loadUiSetting<number>(PAGE_SIZE_KEY, 50)
    return Number.isFinite(v) && v > 0 ? v : 50
  })
  const [pageSizeInput, setPageSizeInput] = useState<string>(() => String(limit))
  const pageSizeTimer = useRef<NodeJS.Timeout | null>(null)
  const [addColPos, setAddColPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

  // Data
  const [rows, setRows]           = useState<DailyWorkTimeRow[]>([])
  const [serverTotals, setServerTotals] = useState<Record<string, number>>({})
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit, totalPages: 0 })

  // Reference data
  const [users, setUsers] = useState<User[]>([])
  const [timeConfig, setTimeConfig] = useState<TenantTimeConfig | null>(null)

  // Column layout
  const availableColumns = buildAvailableColumns(t, timeConfig)
  const [columnLayout, setColumnLayout]       = useState<ColumnLayout>(() => loadColumnLayout(availableColumns))
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [addColDropdownOpen, setAddColDropdownOpen] = useState(false)
  const addColRef = useRef<HTMLDivElement>(null)

  const columnWidths = columnLayout.columnWidths ?? {}
  const sortConfig   = columnLayout.sortConfig ?? null

  // DnD state
  const draggedKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  // Column resize state
  const resizeRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null)

  // UI state
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState<string | null>(null)

  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  // Persist column layout
  // AP-23: `…IfChanged` — dieser Effekt läuft auch beim Mount, und das bloße
  // Öffnen der Liste darf keine eigene Einstellung anlegen (sonst griffe der
  // Mandanten-Standard für diesen Benutzer nie wieder).
  useEffect(() => { saveColumnLayout(columnLayout) }, [columnLayout])

  // Close add-column dropdown on outside click
  useEffect(() => {
    if (!addColDropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (addColRef.current && !addColRef.current.contains(e.target as Node)) setAddColDropdownOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [addColDropdownOpen])

  // Column resize mouse handlers
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return
      const delta = e.clientX - resizeRef.current.startX
      const newWidth = Math.max(60, resizeRef.current.startWidth + delta)
      setColumnLayout((prev) => ({
        ...prev,
        columnWidths: { ...(prev.columnWidths ?? {}), [resizeRef.current!.key]: newWidth },
      }))
    }
    const onUp = () => { resizeRef.current = null; document.body.style.cursor = '' }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  // ── Column helpers ────────────────────────────────────────────────────────────
  const shownColumns = columnLayout.columnKeys
    .map((k) => availableColumns.find((c) => c.key === k))
    .filter(Boolean) as ColumnFieldDef[]

  const availableToAddColumns = availableColumns.filter((c) => !columnLayout.columnKeys.includes(c.key))

  const groupedAvailableColumns = availableToAddColumns.reduce<Record<string, ColumnFieldDef[]>>((acc, c) => {
    if (!acc[c.category]) acc[c.category] = []
    acc[c.category].push(c)
    return acc
  }, {})

  const handleAddColumn = (key: string) => {
    setColumnLayout((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
    setAddColDropdownOpen(false)
  }

  const handleRemoveColumn = (key: string) => {
    setColumnLayout((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== key) }))
  }

  // ── Drag & Drop ───────────────────────────────────────────────────────────────
  const handleDragStart = (key: string) => { draggedKeyRef.current = key }
  const handleDragOver  = (e: React.DragEvent, key: string) => {
    e.preventDefault()
    if (draggedKeyRef.current && draggedKeyRef.current !== key) setDragOverKey(key)
  }
  const handleDrop = (targetKey: string) => {
    const srcKey = draggedKeyRef.current
    if (!srcKey || srcKey === targetKey) { setDragOverKey(null); return }
    setColumnLayout((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to   = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
    draggedKeyRef.current = null
    setDragOverKey(null)
  }
  const handleDragEnd = () => { draggedKeyRef.current = null; setDragOverKey(null) }

  // ── Sort ──────────────────────────────────────────────────────────────────────
  const handleSort = (key: string) => {
    if (!isSortableKey(key)) return
    setColumnLayout((prev) => {
      const current = prev.sortConfig
      let next: ColumnSortConfig | null
      if (!current || current.key !== key) next = { key, dir: 'asc' }
      else if (current.dir === 'asc') next = { key, dir: 'desc' }
      else next = null
      return { ...prev, sortConfig: next }
    })
    // Only reset page for server-side sort (client-side sort acts on loaded data only)
    if (isServerSortable(key)) setPage(1)
  }

  // ── Resize ────────────────────────────────────────────────────────────────────
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault(); e.stopPropagation()
    const th = (e.target as HTMLElement).closest('th')
    const startWidth = th ? th.getBoundingClientRect().width : (columnWidths[key] ?? 120)
    resizeRef.current = { key, startX: e.clientX, startWidth }
    document.body.style.cursor = 'col-resize'
  }

  // ── Fetch rows ────────────────────────────────────────────────────────────────
  const fetchRows = useCallback(
    async (currentFilters: Record<string, ActiveFilter>, currentPage: number, currentSort?: ColumnSortConfig | null, currentLimit: number = limit) => {
      setLoading(true)
      setError(null)
      try {
        const apiParams = filtersToApiParams(currentFilters)
        // Only server-sortable keys are sent to the API; client-sortable keys are handled locally
        const sortParams = currentSort && SERVER_SORTABLE_KEYS.has(currentSort.key)
          ? { sortBy: currentSort.key, sortDir: currentSort.dir }
          : {}
        const { data } = await timeEntriesApi.dailyWorkTimes({
          ...apiParams,
          ...sortParams,
          page: currentPage,
          limit: currentLimit,
        } as Parameters<typeof timeEntriesApi.dailyWorkTimes>[0])
        setRows(data.data)
        setPagination({ total: data.meta.total, page: data.meta.page, limit: data.meta.limit, totalPages: data.meta.totalPages })
        setServerTotals(((data.meta as { totals?: Record<string, number> }).totals) ?? {})
      } catch (err) {
        console.error('Failed to fetch daily work times', err)
        setError('Tagesarbeitszeiten konnten nicht geladen werden.')
      } finally {
        setLoading(false)
      }
    },
    [limit]
  )

  const handleFiltersChange = useCallback(
    (newValues: Record<string, ActiveFilter>) => {
      setFilterValues(newValues)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => fetchRows(newValues, 1), 500)
    },
    [fetchRows]
  )

  const handlePageChange = useCallback(
    (newPage: number) => { setPage(newPage); fetchRows(filterValues, newPage, sortConfig) },
    [filterValues, fetchRows, sortConfig]
  )

  const handlePageSizeChange = useCallback(
    (newLimit: number) => {
      if (!Number.isFinite(newLimit) || newLimit < 1) return
      saveUiSetting(PAGE_SIZE_KEY, newLimit)
      setLimit(newLimit)
      setPageSizeInput(String(newLimit))
      setPage(1)
      fetchRows(filterValues, 1, sortConfig, newLimit)
    },
    [filterValues, fetchRows, sortConfig]
  )

  const handlePageSizeInput = useCallback(
    (val: string) => {
      setPageSizeInput(val)
      if (pageSizeTimer.current) clearTimeout(pageSizeTimer.current)
      pageSizeTimer.current = setTimeout(() => {
        const n = parseInt(val, 10)
        if (Number.isFinite(n) && n >= 1 && n !== limit) handlePageSizeChange(n)
      }, 500)
    },
    [handlePageSizeChange, limit]
  )

  // ── Load reference data ───────────────────────────────────────────────────────
  // timeConfig and users are loaded separately so a failure in one does not
  // prevent the other from being set (e.g. dailyWorkTimes 403/500 must not keep
  // timeConfig null and suppress LV columns).
  useEffect(() => {
    const load = async () => {
      // 1) TenantTimeConfig – needed for LV column definitions.
      // clock-settings statt GET /time-config: die Seite ist ab MANAGER
      // erreichbar, GET /time-config ist ADMIN-only (403 → keine LV-Spalten).
      try {
        const configRes = await timeConfigApi.getClockSettings()
        setTimeConfig(configRes.data)
      } catch (err) {
        console.error('Failed to load time config', err)
      }

      // 2) Mitarbeiterliste via usersApi (limit 100, analog zur SummaryPage)
      try {
        const usersRes = await usersApi.list({ limit: 100, kind: 'EMPLOYEE' })
        setUsers(usersRes.data.data)
      } catch (err) {
        console.warn('Could not load users for filter dropdown', err)
      }
    }
    load()
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRows(filterValues, page, sortConfig) }, [fetchRows])

  // Re-fetch only when a server-sortable column changes (client-sortable is handled by displayRows)
  useEffect(() => {
    if (!sortConfig || isServerSortable(sortConfig.key)) {
      fetchRows(filterValues, page, sortConfig)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(AP-08): Abhaengigkeiten pruefen; Ergaenzen kann zusaetzliche Laeufe ausloesen
  }, [sortConfig])

  // ── Format helpers ────────────────────────────────────────────────────────────
  // Dauer gemäß tenant-weitem Stunden-Format (Dezimal ohne überflüssige Nullen / H:MM)
  const formatMinutes = (minutes: number): string => formatHoursWithUnit(minutes, hoursFormat)
  const formatDate = (dateStr: string): string => fmtDateLong(dateStr)
  const employeeName = (row: DailyWorkTimeRow): string => {
    if (!row.user) return row.userId
    return row.user.profile?.firstName || row.user.profile?.lastName
      ? `${row.user.profile?.firstName ?? ''} ${row.user.profile?.lastName ?? ''}`.trim()
      : row.user.email
  }

  // ── Cell renderer ─────────────────────────────────────────────────────────────
  const renderCell = (row: DailyWorkTimeRow, col: ColumnFieldDef): React.ReactNode => {
    const dash = <span className="text-muted-foreground">-</span>
    switch (col.key) {
      case 'employee':    return employeeName(row)
      case 'vorname':     return row.user?.profile?.firstName ?? dash
      case 'nachname':    return row.user?.profile?.lastName  ?? dash
      case 'date':        return formatDate(row.date)
      case 'entryCount':  return <span className="tabular-nums">{row.entryCount}</span>
      case 'totalMinutes': return <span className="tabular-nums">{formatMinutes(row.totalMinutes)}</span>
      case 'breakMinutes': return row.breakMinutes > 0
        ? <span className="tabular-nums">{row.breakMinutes}min</span>
        : dash
      case 'netMinutes':  return <span className="tabular-nums font-medium">{formatMinutes(row.netMinutes)}</span>
      case 'absenceMinutes': return row.absenceMinutes > 0
        ? <span className="tabular-nums">{formatMinutes(row.absenceMinutes)}</span>
        : dash
      case 'absenceDescription': return row.absenceDescription
        ? <span>{row.absenceDescription}</span>
        : dash
      case 'totalWithAbsenceMinutes': return <span className="tabular-nums font-medium">{formatMinutes(row.totalWithAbsenceMinutes)}</span>
      default:
        if (col.key.startsWith('lv_')) {
          const fieldName = col.key.slice(3)
          const val = row.lvFields?.[fieldName]
          if (val == null) return dash
          if (col.fieldType === 'checkbox') return val === true || val === 'true' ? 'Ja' : 'Nein'
          return String(val)
        }
        return dash
    }
  }

  // ── Client-side Filterung ────────────────────────────────────────────────────
  // • lv_* Felder: werden client-seitig gefiltert (Backend hat keinen LV-Feldfilter)
  // • userId/userId is_not: wird serverseitig via userId/userIds/userIdNot gehandhabt
  const filteredRows = rows.filter((row) => {
    for (const [key, active] of Object.entries(filterValues)) {
      if (!active.value) continue

      // LV-Felder (lv_*) – client-seitig
      if (key.startsWith('lv_')) {
        const fieldName = key.slice(3)
        const cellVal = row.lvFields?.[fieldName]
        const cellStr = cellVal != null ? String(cellVal).toLowerCase() : ''
        const filterStr = active.value.toLowerCase()
        const op = active.operator
        if (op === 'gt' || op === 'lt') {
          const cellNum = parseFloat(String(cellVal ?? ''))
          const filterNum = parseFloat(active.value)
          if (isNaN(cellNum) || isNaN(filterNum)) return false
          if (op === 'gt' && !(cellNum > filterNum)) return false
          if (op === 'lt' && !(cellNum < filterNum)) return false
        } else if (op === 'is' || op === 'is_not') {
          const match = cellStr === filterStr
          if (op === 'is_not' ? match : !match) return false
        } else if (op === 'contains') {
          if (!cellStr.includes(filterStr)) return false
        } else if (op === 'not_contains') {
          if (cellStr.includes(filterStr)) return false
        }
      }
    }
    return true
  })

  // ── Client-side Sortierung ────────────────────────────────────────────────────
  // Handles: vorname/nachname (string), numeric columns, lv_* (string or number)
  const displayRows = sortConfig && isClientSortable(sortConfig.key)
    ? [...filteredRows].sort((a, b) => {
        const key = sortConfig.key
        let aVal: string | number
        let bVal: string | number
        if (key.startsWith('lv_')) {
          const fieldName = key.slice(3)
          aVal = a.lvFields?.[fieldName] != null ? String(a.lvFields[fieldName]) : ''
          bVal = b.lvFields?.[fieldName] != null ? String(b.lvFields[fieldName]) : ''
          // Try numeric compare first
          const aN = parseFloat(aVal as string), bN = parseFloat(bVal as string)
          if (!isNaN(aN) && !isNaN(bN)) {
            return sortConfig.dir === 'asc' ? aN - bN : bN - aN
          }
        } else if (key === 'vorname') {
          aVal = a.user?.profile?.firstName ?? ''
          bVal = b.user?.profile?.firstName ?? ''
        } else if (key === 'nachname') {
          aVal = a.user?.profile?.lastName ?? ''
          bVal = b.user?.profile?.lastName ?? ''
        } else {
          aVal = (a[key as keyof DailyWorkTimeRow] as number) ?? 0
          bVal = (b[key as keyof DailyWorkTimeRow] as number) ?? 0
        }
        if (typeof aVal === 'string' || typeof bVal === 'string') {
          const cmp = String(aVal).localeCompare(String(bVal), 'de', { sensitivity: 'base' })
          return sortConfig.dir === 'asc' ? cmp : -cmp
        }
        return sortConfig.dir === 'asc' ? aVal - bVal : bVal - aVal
      })
    : filteredRows

  // ── Totals row ────────────────────────────────────────────────────────────────
  const totals: Record<string, string> = {}
  const totalsMinutes: Record<string, number> = {}
  for (const col of shownColumns) {
    const v = serverTotals[col.key]
    if (v == null) continue
    if (col.category === 'time' || MINUTE_COLUMN_KEYS.has(col.key)) {
      totalsMinutes[col.key] = v
    } else if (col.fieldType === 'checkbox') {
      totals[col.key] = v > 0 ? `${v} × Ja` : ''
    } else {
      totals[col.key] = Number.isInteger(v) ? v.toLocaleString(navigator.language) : v.toLocaleString(navigator.language, { maximumFractionDigits: 2 })
    }
  }

  // 1 (date) + configurable columns + add-column th (config mode only) + trailing empty th
  const totalColCount = 1 + shownColumns.length + (columnConfigMode && availableToAddColumns.length > 0 ? 1 : 0) + 1

  const availableFilterFields = buildAvailableFilterFields(t, users, timeConfig)

  // ── Export ───────────────────────────────────────────────────────────────────
  const handleExport = (format: 'xlsx' | 'docx' | 'pdf', orientation?: 'portrait' | 'landscape') => {
    const dateFrom = filterValues['dateFrom']?.value?.slice(0, 10) as string | undefined
    const dateTo   = filterValues['dateTo']?.value?.slice(0, 10)   as string | undefined
    const userId   = filterValues['userId']?.value as string | undefined

    // Sichtbare Spalten (ohne 'date' – wird Backend-seitig immer vorne eingefügt)
    // summable + align mitschicken, damit Backend Summenzeile korrekt bilden kann
    const columns = JSON.stringify(
      shownColumns.map((c) => ({ key: c.key, label: c.label, summable: c.summable ?? false, align: c.align }))
    )

    // Aktive Sortierung
    const sortBy  = sortConfig?.key
    const sortDir = sortConfig?.dir

    // Lesbarer Filtertext für die Infozeile über der Tabelle
    const filterParts: string[] = []
    for (const [key, active] of Object.entries(filterValues)) {
      if (!active.value) continue
      const fieldDef = availableFilterFields.find((f) => f.key === key)
      const fieldLabel = fieldDef?.label ?? key
      if (key === 'userId') {
        const ids = active.value.split(',').filter(Boolean)
        const names = ids.map((id) => {
          const u = users.find((u) => u.id === id)
          return u ? (u.profile?.firstName ? `${u.profile.firstName}${u.profile.lastName ? ' ' + u.profile.lastName : ''}` : u.email) : id
        }).join(', ')
        const opLabel2 = active.operator === 'is_not' ? '≠' : '='
        filterParts.push(`${fieldLabel} ${opLabel2} ${names}`)
      } else if (key === 'dateFrom') {
        filterParts.push(`Von: ${active.value.slice(0, 10)}`)
      } else if (key === 'dateTo') {
        filterParts.push(`Bis: ${active.value.slice(0, 10)}`)
      } else {
        const opLabel: Record<string, string> = {
          is: '=', is_not: '≠', contains: '~', not_contains: '!~', gt: '>', lt: '<',
        }
        filterParts.push(`${fieldLabel} ${opLabel[active.operator] ?? active.operator} ${active.value}`)
      }
    }
    const filterInfo = filterParts.length > 0 ? filterParts.join(' · ') : undefined

    return timeEntriesApi.dailyWorkTimesExportRequest({ dateFrom, dateTo, userId, format, orientation, columns, sortBy, sortDir, filterInfo })
  }

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className={pageWide}>
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tagesarbeitszeiten</h1>
          <p className="mt-1 text-sm text-muted-foreground">Tagesarbeitszeit und Lohnverrechnungsfelder pro Mitarbeiter</p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <ExportButton
              request={handleExport}
              defaultFilename="lohnverrechnung"
              label="Exportieren"
              mail={{
                source: 'EXPORT_DAILY_WORK_TIMES',
                title: t('mailExport.titles.EXPORT_DAILY_WORK_TIMES'),
                subject: betreffMitZeitraum(t('mailExport.titles.EXPORT_DAILY_WORK_TIMES'), filterValues['dateFrom']?.value, filterValues['dateTo']?.value),
              }}
            />
          )}
        </div>
      </div>

      {/* FilterBar */}
      <FilterBar
        availableFields={availableFilterFields}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="payroll"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
      />

      {/* Table card – relative so the gear button can be positioned absolutely */}
      <div className="relative rounded-lg border border-border bg-card shadow-sm overflow-hidden">

        {/* Gear icon – top-right, only toggles config mode (does NOT open dropdown) */}
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title={columnConfigMode ? 'Fertig' : 'Spalten anpassen'}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>

        {/* Hilfe „?" – links neben dem Spalten-Zahnrad */}
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" hilfe={HILFE_VERWEISE.tagesarbeitszeiten} />

        {/* AP-23 Phase D: Zurücksetzen / als Mandanten-Standard speichern */}
        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={columnLayout}
                    onReset={(value) => {
                const next = (value as ColumnLayout | null) ?? {
                  columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null,
                }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColumnLayout(next)
              }}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-4 py-3 text-sm text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400 border-b border-border">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Date column (always first) – sortable + resizable, never draggable */}
                <th
                  className="relative px-3 py-1 text-left font-medium text-foreground"
                  style={columnWidths['date'] ? { width: columnWidths['date'], minWidth: columnWidths['date'] } : undefined}
                >
                  <button
                    type="button"
                    onClick={() => handleSort('date')}
                    className="flex items-center gap-1 hover:text-primary"
                  >
                    Datum
                    {sortConfig?.key === 'date'
                      ? (sortConfig.dir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />)
                      : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                  </button>
                  <div
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                    onMouseDown={(e) => handleResizeStart(e, 'date')}
                  />
                </th>

                {/* Configurable columns */}
                {shownColumns.map((col) => {
                  const isSortable = isSortableKey(col.key) && !columnConfigMode
                  const isDragOver = dragOverKey === col.key
                  const colWidth = columnWidths[col.key]
                  return (
                    <th
                      key={col.key}
                      style={colWidth ? { width: colWidth, minWidth: colWidth } : undefined}
                      className={`relative select-none px-3 py-1 font-medium text-foreground
                        ${col.align === 'right' ? 'text-right' : 'text-left'}
                        ${columnConfigMode ? 'cursor-grab' : ''}
                        ${isDragOver ? 'bg-primary/10 outline outline-2 outline-primary' : ''}
                      `}
                      draggable={columnConfigMode}
                      onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                      onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                      onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                      onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    >
                      <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                        {isSortable ? (
                          <button
                            type="button"
                            onClick={() => handleSort(col.key)}
                            className="flex items-center gap-1 hover:text-primary"
                          >
                            {col.label}
                            {sortConfig?.key === col.key
                              ? (sortConfig.dir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />)
                              : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                          </button>
                        ) : (
                          <span>{col.label}</span>
                        )}
                        {columnConfigMode && (
                          <button
                            type="button"
                            onClick={() => handleRemoveColumn(col.key)}
                            className="ml-1 rounded p-0.5 text-destructive hover:text-destructive/80 text-xs"
                          >✕</button>
                        )}
                      </div>
                      <div
                        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                        onMouseDown={(e) => handleResizeStart(e, col.key)}
                      />
                    </th>
                  )
                })}

                {/* Config mode: add column button in header */}
                {columnConfigMode && availableToAddColumns.length > 0 && (
                  <th className="px-2 py-1.5">
                    <div className="relative" ref={addColRef}>
                      <button
                        type="button"
                        onClick={() => { const r = addColRef.current?.getBoundingClientRect(); if (r) setAddColPos({ top: r.bottom + 4, left: r.left }); setAddColDropdownOpen((v) => !v) }}
                        className="whitespace-nowrap rounded border border-dashed border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        + Spalte
                      </button>
                      {addColDropdownOpen && (
                        <div className="fixed z-50 max-h-64 min-w-[200px] overflow-y-auto rounded-md border border-border bg-background shadow-lg" style={{ top: addColPos.top, left: addColPos.left }}>
                          {Object.entries(groupedAvailableColumns).map(([cat, cols]) => (
                            <div key={cat}>
                              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {COLUMN_CATEGORY_LABELS[cat] ?? cat}
                              </div>
                              {cols.map((col) => (
                                <button
                                  key={col.key}
                                  type="button"
                                  onClick={() => handleAddColumn(col.key)}
                                  className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted"
                                >
                                  {col.label}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </th>
                )}

                {/* Trailing spacer */}
                <th className="w-4" />
              </tr>
            </thead>

            <tbody>
              {loading && displayRows.length === 0 && (
                <tr><td colSpan={totalColCount} className="px-6 py-8 text-center text-muted-foreground text-sm">Laden…</td></tr>
              )}
              {!loading && displayRows.length === 0 && (
                <tr><td colSpan={totalColCount} className="px-6 py-8 text-center text-muted-foreground text-sm">Keine Einträge gefunden.</td></tr>
              )}

              {displayRows.map((row, idx) => (
                <tr
                  key={`${row.userId}_${row.date}`}
                  className={`border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} hover:bg-accent/30 transition-colors`}
                >
                  {/* Date */}
                  <td className="px-3 py-1 tabular-nums whitespace-nowrap text-muted-foreground">
                    {formatDate(row.date)}
                  </td>

                  {/* Configurable columns */}
                  {shownColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-3 py-1 whitespace-nowrap ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}
                    >
                      {renderCell(row, col)}
                    </td>
                  ))}

                  {/* Trailing spacer */}
                  <td />
                </tr>
              ))}

              {/* Totals row – entry count shown under the date column */}
              {filteredRows.length > 0 && pagination.page >= pagination.totalPages && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="px-3 py-1 text-muted-foreground whitespace-nowrap">
                    {loading ? 'Laden…' : `Summe (${pagination.total} Einträge)`}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-3 py-1 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {totalsMinutes[col.key] != null ? (
                        <div className="flex flex-col items-end leading-tight">
                          <span>{formatMinutes(totalsMinutes[col.key])}</span>
                          {hoursFormat === 'hhmm' && (
                            <span className="text-[11px] font-normal text-muted-foreground">{formatHours(totalsMinutes[col.key], 'decimal')} h</span>
                          )}
                        </div>
                      ) : (totals[col.key] ?? '')}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Seitengröße */}
        <div className="flex items-center gap-2 px-4 py-2 border-t border-border text-xs text-muted-foreground">
          <label htmlFor="payroll-pagesize">Zeilen pro Seite</label>
          <input id="payroll-pagesize" type="number" min={1} list="payroll-pagesize-options" value={pageSizeInput} onChange={(e) => handlePageSizeInput(e.target.value)} className="w-24 rounded-md border border-input bg-background px-2 py-1 text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          <datalist id="payroll-pagesize-options"><option value="50" /><option value="100" /><option value="200" /></datalist>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground">
              Seite {pagination.page} von {pagination.totalPages} · {pagination.total} Einträge
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="rounded-md px-2.5 py-1 text-xs border border-border hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Zurück
              </button>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= pagination.totalPages}
                className="rounded-md px-2.5 py-1 text-xs border border-border hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Weiter →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
